import React, { useState } from 'react';
import { theme } from '../../theme';
import { Avatar } from '../ui/Avatar';

export interface PollVoter {
  userId: string;
  name?: string;
  avatar?: string | null;
}
export interface PollOptionData {
  id: string;
  label: string;
  joinsMatch: boolean;
  count: number;
  voters: PollVoter[];
}
export interface PollData {
  id: string;
  groupId: string;
  question: string;
  allowMultiple: boolean;
  matchId?: string | null;
  closesAt?: string | null;
  closedAt?: string | null;
  createdAt: string;
  creator?: { id: string; name: string; avatar?: string | null } | null;
  totalVotes: number;
  options: PollOptionData[];
  myOptionIds: string[];
}

interface PollCardProps {
  poll: PollData;
  currentUserId?: string;
  isAdmin?: boolean;
  voting?: boolean;
  onVote: (pollId: string, optionId: string) => void;
  onClose: (pollId: string) => void;
  onDelete: (pollId: string) => void;
}

export const PollCard: React.FC<PollCardProps> = ({ poll, currentUserId, isAdmin, voting, onVote, onClose, onDelete }) => {
  const [showVoters, setShowVoters] = useState<string | null>(null);
  const isClosed = !!poll.closedAt || (!!poll.closesAt && new Date(poll.closesAt) < new Date());
  const canManage = isAdmin || poll.creator?.id === currentUserId;
  const canClose = !isClosed && canManage;
  const maxCount = Math.max(1, ...poll.options.map((o) => o.count));

  return (
    <div style={{
      border: `1px solid ${theme.colors.border}`,
      background: theme.colors.background,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 18 }}>📊</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: theme.colors.text, fontWeight: 700, fontSize: 15 }}>{poll.question}</div>
          <div style={{ color: theme.colors.textSecondary, fontSize: 12, marginTop: 2 }}>
            {poll.creator?.name ? `${poll.creator.name} · ` : ''}
            {poll.totalVotes} voto{poll.totalVotes === 1 ? '' : 's'}
            {poll.matchId ? ' · 🔗 sincroniza con el partido' : ''}
            {isClosed ? ' · cerrada' : ''}
            {poll.allowMultiple ? ' · multi' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {canClose && (
            <button
              type="button"
              onClick={() => onClose(poll.id)}
              title="Cerrar encuesta"
              style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
            >
              Cerrar
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => onDelete(poll.id)}
              title="Eliminar encuesta"
              style={{ background: 'transparent', border: 'none', color: '#EF5350', cursor: 'pointer', fontSize: 16, padding: 2, lineHeight: 1 }}
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        {poll.options.map((o) => {
          const mine = poll.myOptionIds.includes(o.id);
          const pct = poll.totalVotes > 0 ? Math.round((o.count / maxCount) * 100) : 0;
          return (
            <div key={o.id} style={{ marginBottom: 8 }}>
              <button
                type="button"
                disabled={isClosed || voting}
                onClick={() => onVote(poll.id, o.id)}
                style={{
                  position: 'relative',
                  width: '100%',
                  textAlign: 'left',
                  border: `1px solid ${mine ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: theme.colors.surface,
                  cursor: isClosed || voting ? 'default' : 'pointer',
                  overflow: 'hidden',
                }}
              >
                {/* barra de fondo proporcional */}
                <span style={{
                  position: 'absolute', top: 0, left: 0, bottom: 0, width: `${pct}%`,
                  background: o.joinsMatch ? '#00C85322' : `${theme.colors.primary}1F`,
                  transition: 'width 0.3s',
                }} />
                <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: poll.allowMultiple ? 4 : 9, flexShrink: 0,
                    border: `2px solid ${mine ? theme.colors.primary : theme.colors.border}`,
                    background: mine ? theme.colors.primary : 'transparent',
                    color: theme.colors.background, fontSize: 12, fontWeight: 900,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>{mine ? '✓' : ''}</span>
                  <span style={{ flex: 1, color: theme.colors.text, fontSize: 14, fontWeight: mine ? 700 : 500 }}>
                    {o.joinsMatch ? '⚽ ' : ''}{o.label}
                  </span>
                  <span style={{ color: theme.colors.textSecondary, fontSize: 13, fontWeight: 600 }}>{o.count}</span>
                </span>
              </button>
              {/* quién votó (estilo WhatsApp) */}
              {o.voters.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, paddingLeft: 2, flexWrap: 'wrap' }}>
                  {o.voters.slice(0, showVoters === o.id ? o.voters.length : 6).map((v) => (
                    <span key={v.userId} title={v.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Avatar src={v.avatar} name={v.name} size={20} bg={theme.colors.primary} />
                    </span>
                  ))}
                  {o.voters.length > 6 && showVoters !== o.id && (
                    <button type="button" onClick={() => setShowVoters(o.id)} style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, fontSize: 12, cursor: 'pointer' }}>
                      +{o.voters.length - 6}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
